import React from "react";
import { Card } from "./card";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useResponsive } from "@/lib/responsive";
import { useGameResponsive } from "@/hooks/use-game-responsive";


interface PlayerHandProps {
  cards: string[];
  isCurrentTurn: boolean;
  noAnimation?: boolean;
  isUnder10?: boolean;
  onUnder10Replace?: () => void;
  under10ButtonShown?: boolean;
  onCardPlay?: (card: string) => void;
}

export function PlayerHand({
  cards,
  isCurrentTurn,
  noAnimation = false,
  isUnder10 = false,
  onUnder10Replace,
  under10ButtonShown = false,
  onCardPlay,
}: PlayerHandProps) {
  // Local state for selected card
  const [selectedCard, setSelectedCard] = React.useState<string | null>(null);
  // Sort cards by suit and value
  const sortedCards = [...(cards || [])].sort((a, b) => {
    const [suitA, valueA] = [a.charAt(0), parseInt(a.slice(1))];
    const [suitB, valueB] = [b.charAt(0), parseInt(b.slice(1))];
    if (suitA !== suitB) return suitA.localeCompare(suitB);
    return valueA - valueB;
  });

  const randomizedCards = [...(cards || [])];
    for (let i = randomizedCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [randomizedCards[i], randomizedCards[j]] = [randomizedCards[j], randomizedCards[i]];
    }

  // Handle card click: select or play
  const handleCardClick = (card: string) => {
    if (!isCurrentTurn) return;
    if (selectedCard === card) {
      // Play the card
      if (onCardPlay) onCardPlay(card);
      setSelectedCard(null);
    } else {
      setSelectedCard(card);
    }
  };

  // Use our game-specific responsive hook
  const {
    isPortrait,
    isLandscape,
    getCardSize,
    getSpacing,
    getAnimationSpeed,
    getOptimalCardSize,
    screenSize
  } = useGameResponsive();
  
  return (
    <motion.div
      initial={noAnimation ? false : { opacity: 0, y: 100 }}
      animate={noAnimation ? {} : { opacity: 1, y: 0 }}
      className={cn(
        "player-hand fixed left-0 right-0 transition-all duration-300 bottom-0 overflow-visible",
        // Add hover behavior to raise hand when hovered
        isLandscape 
          ? "hover:-translate-y-[calc(6vh)]" 
          : "hover:-translate-y-[calc(8vh)]",
        // Conditional padding based on screen dimensions and orientation - removed bottom padding
        isLandscape 
          ? "px-[calc(0.5vh)] pt-[calc(1vh)] pb-0" 
          : "px-[calc(0.5vh)] pt-[calc(2vh)] pb-0",
        // Add background highlight when it's the player's turn
        isCurrentTurn ? "theme-bg-primary/5" : ""
      )}
      style={{
        // No negative bottom positioning needed since we're anchoring to bottom
      }}
    >
      
      
      <motion.div
        className={cn(
          "flex justify-center overflow-x-auto overflow-y-visible",
          // Responsive padding based on screen dimensions - removed bottom padding and added top margin for card elevation
          isLandscape 
            ? "pb-0 pt-[calc(7vh)]" 
            : "pb-0 pt-[calc(9vh)]"
        )}
        style={{
          // Use game-specific spacing from our new hook
          gap: `${getSpacing()}px`,
        }}
        variants={noAnimation ? undefined : {
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        initial={noAnimation ? false : "hidden"}
        animate={noAnimation ? {} : "show"}
      >
        {sortedCards.map((card, index) => (
          <motion.div
            key={card}
            variants={noAnimation ? undefined : {
              hidden: {
                opacity: 0,
                y: 100,
                // No rotation
              },
              show: {
                opacity: 1,
                y: 0,
                // No rotation
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                },
              },
            }}
            className="relative"
            style={{
              // No rotation - as per request
              transform: "none",
              // Maintain transform origin at bottom center
              transformOrigin: "bottom center",
              // Adjust z-index for proper card stacking
              zIndex: card === selectedCard ? 10 : 5 - Math.abs(index - (sortedCards.length - 1) / 2)
            }}
          >
            {isCurrentTurn && (
              <div className="absolute inset-x-0 -top-3 h-8 bg-gradient-to-b from-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-t-lg" />
            )}
            
            <div className={cn(
              "group relative transition-all duration-300",
              // Responsive hover transform using IIFE to avoid nested ternaries
              isCurrentTurn ? (() => {
                if (isPortrait) return "hover:-translate-y-[calc(3vh)]";
                return "hover:-translate-y-[calc(4vh)]";
              })() : "",
              // Responsive selection transform using IIFE to avoid nested ternaries
              card === selectedCard ? (() => {
                if (isPortrait) return "-translate-y-[calc(5vh)]";
                return "-translate-y-[calc(6vh)]";
              })() : "",
            )}>
              <Card
                value={card}
                onClick={() => handleCardClick(card)}
                onDragPlay={() => handleCardClick(card)}
                disabled={!isCurrentTurn}
                selected={card === selectedCard}
                className={cn(
                  // Use our game-specific responsive utility to get card size classes
                  (() => {
                    // Get sizes from our game hook
                    const cardSize = getCardSize();
                    // Create tailwind-compatible sizing classes
                    return `w-[${cardSize.width}px] h-[${cardSize.height}px]`;
                  })(),
                  "transition-all",
                  isCurrentTurn && "cursor-pointer",
                  card === selectedCard && "z-10"
                )}
                noAnimation={noAnimation}
                draggable={isCurrentTurn}
              />
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}
